import { ResultOf } from 'gql.tada';
import { sortOptionsFragment } from './graphql/sortOptionsFragment';
import { SearchModalsContentQuery } from './graphql/searchModalsContentQuery';
import { BadgeVariant } from './app-kit/helpers/mapBadgeVariant';
import { SelectOption } from '@property.com.au/ui-kit';
import { weblinkFragment } from './graphql/weblinkFragment';
import { buttonFragment } from './graphql/buttonFragment';
import { ellipsesIconFragment } from './graphql/ellipsisIconFragment';
import { paginationFragment } from './graphql/paginationFragment';

export type SearchPageSortOptions = ResultOf<typeof sortOptionsFragment>;

export type SearchPageSortOptionForRentValue = NonNullable<
  NonNullable<NonNullable<SearchPageSortOptions>['forRent']>['options']
>[number]['value'];

export const SearchPageSortOptionForRentValues: SearchPageSortOptionForRentValue[] = [
  'NEW_ASC',
  'NEW_DESC',
  'NEXT_INSPECTION_TIME',
  'PRICE_ASC',
  'PRICE_DESC',
  'RELEVANCE',
];

export type SearchPageSortOptionForAllPropertyStatusesValue = NonNullable<
  NonNullable<NonNullable<SearchPageSortOptions>['allPropertyStatuses']>['options']
>[number]['value'];

export const SearchPageSortOptionAllPropertyStatusesValues: SearchPageSortOptionForAllPropertyStatusesValue[] =
  ['PRICE_ASC', 'PRICE_DESC', 'RELEVANCE'];

export type SearchPageSortOptionForSaleValue = NonNullable<
  NonNullable<NonNullable<SearchPageSortOptions>['forSale']>['options']
>[number]['value'];

export const SearchPageSortOptionForSaleValues: SearchPageSortOptionForSaleValue[] = [
  'NEW_ASC',
  'NEW_DESC',
  'NEXT_INSPECTION_TIME',
  'PRICE_ASC',
  'PRICE_DESC',
  'RELEVANCE',
  'NEXT_AUCTION_TIME',
];

export type SearchPageSortOptionSoldValue = NonNullable<
  NonNullable<NonNullable<SearchPageSortOptions>['sold']>['options']
>[number]['value'];

export const SearchPageSortOptionSoldValues: SearchPageSortOptionSoldValue[] = [
  'NEW_DESC',
  'PRICE_ASC',
  'PRICE_DESC',
  'RELEVANCE',
];

export type SearchPageSortOptionOffMarketValue = NonNullable<
  NonNullable<NonNullable<SearchPageSortOptions>['offMarket']>['options']
>[number]['value'];

export const SearchPageSortOptionOffMarketValues: SearchPageSortOptionOffMarketValue[] = [
  'PRICE_ASC',
  'PRICE_DESC',
  'RELEVANCE',
];

export enum SearchPropertyStatus {
  all = 'ALL',
  forRent = 'FOR_RENT',
  forSale = 'FOR_SALE',
  offMarket = 'OFF_MARKET',
  sold = 'SOLD',
}

export const searchPropertyStatusValues = Object.values(SearchPropertyStatus);

export const SEARCH_PROPERTY_STATUS_FOR_SALE_VALUE = SearchPropertyStatus.forSale;
export const SEARCH_PROPERTY_STATUS_FOR_RENT_VALUE = SearchPropertyStatus.forRent;
export const SEARCH_PROPERTY_STATUS_OFF_MARKET_VALUE = SearchPropertyStatus.offMarket;
export const SEARCH_PROPERTY_STATUS_ALL_PROPERTIES_VALUE = SearchPropertyStatus.all;

export interface OptionalPropertyFilters {
  propertyTypes: string;
  minBedrooms: string;
  minBathrooms: string;
  minLandSize: string;
  indoorFeatures: string;
  outdoorFeatures: string;
  ecoFriendlyFeatures: string;
  minCarSpaces: string;
  keywords: string;
}

export type SearchPageView = 'map' | 'map-list' | 'list';
type SearchModalsContentQuery = ResultOf<typeof SearchModalsContentQuery>;
export type SearchModalsFilterOptions = NonNullable<
  SearchModalsContentQuery['searchModalsContent']
>['filterOptions'];

export type SearchOptionPropertyType = NonNullable<
  NonNullable<SearchModalsFilterOptions>['propertyTypes']
>[number];
export type SearchOptionIndoorFeature = NonNullable<
  NonNullable<SearchModalsFilterOptions>['indoorFeatures']
>[number];
export type SearchOptionOutdoorFeature = NonNullable<
  NonNullable<SearchModalsFilterOptions>['outdoorFeatures']
>[number];
export type SearchOptionEcoFriendlyFeature = NonNullable<
  NonNullable<SearchModalsFilterOptions>['ecoFriendlyFeatures']
>[number];

export type SearchOptionMinBedrooms = NonNullable<
  NonNullable<SearchModalsFilterOptions>['minBedroomsV2']
>[number];

export type SearchOptionMinBathrooms = NonNullable<
  NonNullable<SearchModalsFilterOptions>['minBathrooms']
>[number];
export type SearchOptionMinCarSpaces = NonNullable<
  NonNullable<SearchModalsFilterOptions>['minCarSpaces']
>[number];
export type SearchOptionFilterPrices = NonNullable<SearchModalsFilterOptions>['minPricesV2'];
export type SearchOptionPropertyStatus = NonNullable<
  NonNullable<SearchModalsFilterOptions>['propertyStatus']
>[number];
type SearchOptionIntValue = NonNullable<SearchOptionFilterPrices>['nonRentalPriceOptions'][number];

export type SearchModalBudgetFilterMinPrices = SearchOptionFilterPrices;
export type SearchModalBudgetFilterMaxPrices = SearchOptionFilterPrices;

export type SearchModalBudgetFilterMinPricesRentalOptions = SearchOptionIntValue[];
export type SearchModalBudgetFilterMinPricesNonRentalOptions = SearchOptionIntValue[];

export type SearchModalBudgetFilterMaxPricesRentalOptions = SearchOptionIntValue[];
export type SearchModalBudgetFilterMaxPricesNonRentalOptions = SearchOptionIntValue[];

export type SearchModalBudgetFilterRentalPriceOptions = {
  minPrices: SearchModalBudgetFilterMinPricesRentalOptions;
  maxPrices: SearchModalBudgetFilterMaxPricesRentalOptions;
};

export type SearchModalBudgetFilterNonRentalPriceOptions = {
  minPrices: SearchModalBudgetFilterMinPricesNonRentalOptions;
  maxPrices: SearchModalBudgetFilterMaxPricesNonRentalOptions;
};

export interface SelectOptionWithBadge extends SelectOption {
  badge: BadgeVariant;
  callout?: {
    variant: BadgeVariant;
    text: string;
  };
}

export type WebLinkFragment = ResultOf<typeof weblinkFragment>;

export type Button = ResultOf<typeof buttonFragment>;

export type EllipsisIconFragment = ResultOf<typeof ellipsesIconFragment>;

export type PaginationFragment = ResultOf<typeof paginationFragment>;

export type PropertyStatus = 'ALL' | 'FOR_RENT' | 'OFF_MARKET' | 'SOLD' | 'FOR_SALE';
export type PropertyType =
  | 'ALL'
  | 'HOUSE'
  | 'UNITAPARTMENT'
  | 'VILLA'
  | 'LAND'
  | 'TOWNHOUSE'
  | 'ACREAGE'
  | 'RURAL'
  | 'UNITBLOCK'
  | 'RETIRE';
export type SortBy =
  | 'NEW_ASC'
  | 'NEW_DESC'
  | 'NEXT_INSPECTION_TIME'
  | 'PRICE_ASC'
  | 'PRICE_DESC'
  | 'RELEVANCE'
  | 'NEXT_AUCTION_TIME';
export type EcoFriendlyFeature =
  | 'AIRCONDITIONING'
  | 'ANY'
  | 'HEATING'
  | 'HIGHENERGYEFFICIENCY'
  | 'SOLARHOTWATER'
  | 'SOLARPANELS'
  | 'WATERTANK';

export type IndoorFeature =
  | 'ALARMSYSTEM'
  | 'ANY'
  | 'BROADBAND'
  | 'BUILTINROBES'
  | 'DISHWASHER'
  | 'ENSUITE'
  | 'FLOORBOARDS'
  | 'GYM'
  | 'RUMPUSROOM'
  | 'STUDY'
  | 'WORKSHOP';

export type OutdoorFeature =
  | 'ANY'
  | 'BALCONY'
  | 'FULLYFENCED'
  | 'GARAGE'
  | 'OUTDOORSPA'
  | 'SHED'
  | 'SWIMMINGPOOL'
  | 'TENNISCOURT'
  | 'UNDERCOVERPARKING';
